import clamp from 'lodash/clamp';
import React from 'react';

interface UseScalingProps {
  imageUrl: string | undefined;
  minWidth: number;
  minHeight: number;
  cropBoxWidth: number;
  cropBoxHeight: number;
  freeScale?: boolean;
}

interface UseScalingState {
  scale: number;
  scaleMin: number;
  scaleMax: number;
  isScaleRangeCalculating: boolean;
}

type UseScalingReturn = [UseScalingState, { setScale: (value: number) => void }];

export const SCALE_DEFAULT = 1.3;
export const SCALE_MIN_DEFAULT = 1.0;
export const SCALE_MAX_DEFAULT = 3.0;
export const SCALE_FREE_MIN = 0.3;

const getDefaultState = ({ imageUrl, freeScale }: Pick<UseScalingProps, 'imageUrl' | 'freeScale'>): UseScalingState => ({
  scaleMin: freeScale ? SCALE_FREE_MIN : SCALE_MIN_DEFAULT,
  scaleMax: SCALE_MAX_DEFAULT,
  scale: SCALE_DEFAULT,
  isScaleRangeCalculating: Boolean(imageUrl),
});

export const useScaling = ({
  imageUrl,
  minWidth,
  minHeight,
  cropBoxWidth,
  cropBoxHeight,
  freeScale = false,
}: UseScalingProps): UseScalingReturn => {
  const [state, setState] = React.useState<UseScalingState>(getDefaultState({ imageUrl, freeScale }));

  React.useEffect(() => {
    let isCurrentEffectInstanceActive = true;

    (async () => {
      const draftState = getDefaultState({ imageUrl, freeScale });

      setState({ ...draftState });

      if (!imageUrl) {
        return;
      }

      const image = new Image();
      const imageLoadingPromise = new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });
      if (!imageUrl.startsWith('data:')) {
        image.crossOrigin = 'anonymous';
      }
      image.src = imageUrl;

      try {
        await imageLoadingPromise;
      } catch (err: unknown) {
        // Image loading error
        console.error(err);
        draftState.isScaleRangeCalculating = false;
        setState({ ...draftState });
      }

      if (!isCurrentEffectInstanceActive) {
        return;
      }

      const reserveMargin = 10;
      const newScaleMaxX = image.width / (minWidth + reserveMargin);
      const newScaleMaxY = image.height / (minHeight + reserveMargin);

      if (freeScale) {
        draftState.scaleMin = SCALE_FREE_MIN;
      } else {
        const newScaleMinX = cropBoxWidth / image.width;
        const newScaleMinY = cropBoxHeight / image.height;
        draftState.scaleMin = Math.max(newScaleMinX, newScaleMinY);
      }

      draftState.scaleMax = Math.max(SCALE_MIN_DEFAULT, Math.min(newScaleMaxX, newScaleMaxY, SCALE_MAX_DEFAULT));
      draftState.scale = clamp(draftState.scale, draftState.scaleMin, draftState.scaleMax);
      draftState.isScaleRangeCalculating = false;

      setState({ ...draftState });
    })();

    return () => {
      isCurrentEffectInstanceActive = false;
    };
  }, [cropBoxHeight, cropBoxWidth, freeScale, imageUrl, minHeight, minWidth]);

  const setScale = React.useCallback<UseScalingReturn[1]['setScale']>((value) => {
    setState((prevState) => {
      const newScale = clamp(value, prevState.scaleMin, prevState.scaleMax);

      if (prevState.scale === newScale) {
        return prevState;
      }

      return {
        ...prevState,
        scale: newScale,
      };
    });
  }, []);

  return [state, { setScale }];
};
